import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Title from '../components/Title';
import SubmitButton from '../components/SubmitButton';
import NavigationStatus from '../components/NavigationStatus';
import useGtmEventTracking from '../components/useGtmEventTracking';

interface RemovalQuestions {
  floor: string;
  hasLift: boolean | null;
  hasFurniture: boolean | null;
  hasDisassembly: boolean | null;
  hasParking: boolean | null;
}

interface RemovalTier {
  id: string;
  name: string;
  price: number;
  description: string;
  imageUrl: string;
  details: string;
}

function RemovalsQuestionsComponent(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const gtmEventTracking = useGtmEventTracking();

  // Get the selected tier from location state
  const selectedTier = (location.state as any)?.selectedTier as RemovalTier;

  const [questions, setQuestions] = useState<RemovalQuestions>({
    floor: '',
    hasLift: null,
    hasFurniture: null,
    hasDisassembly: null,
    hasParking: null,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    gtmEventTracking({ event: 'booking_step_removals_questions' });
  }, [gtmEventTracking]);

  const handleQuestionChange = (field: keyof RemovalQuestions, value: any) => {
    setQuestions(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Reset disassembly if furniture is set to false
    if (field === 'hasFurniture' && value === false) {
      setQuestions(prev => ({
        ...prev,
        hasFurniture: false,
        hasDisassembly: null
      }));
    }
  };

  const isFormValid = () => {
    const floorSelected = questions.floor !== '';
    const liftAnswered = questions.floor === '0' || questions.hasLift !== null;
    const disassemblyAnswered = questions.hasFurniture === false || questions.hasDisassembly !== null;
    return floorSelected && 
           liftAnswered &&
           questions.hasFurniture !== null &&
           disassemblyAnswered &&
           questions.hasParking !== null;
  };

  const handleContinue = () => {
    if (isFormValid()) {
      // Navigate to extras page with all data
      history.push('/removals/extras', {
        selectedTier,
        questions,
        boxes: [], // Removals don't use boxes
        removalService: true
      });
    }
  };

  const floorOptions = [
    { value: '0', label: 'Ground floor' },
    { value: '1', label: '1st floor' },
    { value: '2', label: '2nd floor' },
    { value: '3', label: '3rd floor' },
    { value: '4', label: '4th floor' },
    { value: '5', label: '5th floor' },
    { value: '6', label: '6th floor' },
    { value: '7', label: '7th floor' },
    { value: '8', label: '8th floor' },
    { value: '9', label: '9th floor' },
    { value: '10', label: '10th floor' },
    { value: '11', label: '11th floor' },
    { value: '12', label: '12th floor' },
    { value: '13', label: '13th floor' },
    { value: '14', label: '14th floor' },
    { value: '15', label: '15th floor' },
    { value: '16', label: '16th floor' },
    { value: '17', label: '17th floor' },
    { value: '18', label: '18th floor' },
    { value: '19', label: '19th floor' },
    { value: '20', label: '20th+ floor' },
  ];

  if (!selectedTier) {
    // Redirect back to removals page if no tier selected
    history.replace('/removals');
    return <div>Redirecting...</div>;
  }

  return (
    <>
      <NavigationStatus 
        step={2} 
        locationState={{ selectedTier }}
      />

      <div className="removals-questions-container">
        <Title>Tell us more about your move</Title>
        <p className="grey text-center mb-4">
          Help us provide you with the most accurate quote for your {selectedTier.name.toLowerCase()} removal.
        </p>

        <div className="form">
          <div id="removals-funnel-wrapper" className="sections questions mt-n3 mt-md-0">

            {/* Floor Dropdown */}
            <div className="open transparent dropdown mb-4">
              <div className="card">
                <div className="card-header dropdown-header">
                  <div className="header-title">What floor is it on?</div>
                </div>
                <div className="dropdown-body">
                  <div className="text-start ls-grey1 mb-3">
                    If you're moving from a house, please select 'Ground floor'.
                  </div>
                  <div className="dropdown-content">
                    <div className="form-group col-lg-6 p-0 my-3">
                      <div className="inline-label ls-select form-group">
                        <select
                          className="form-select mb-0 form-control"
                          value={questions.floor}
                          onChange={(e) => handleQuestionChange('floor', e.target.value)}
                        >
                          <option value="">Please select *</option>
                          {floorOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lift Question - Only show if floor is selected and not ground floor */}
            {questions.floor !== '' && questions.floor !== '0' && (
              <div className="open transparent dropdown mb-4">
                <div className="card">
                  <div className="card-header dropdown-header">
                    <div className="header-title">Does your property have a lift?</div>
                  </div>
                  <div className="dropdown-body">
                    <div className="dropdown-content">
                      <div 
                        className={`ls-radio-button ${questions.hasLift === true ? 'selected' : ''}`}
                        onClick={() => handleQuestionChange('hasLift', true)}
                      >
                        <div className="marker"></div>
                        <div className="text text-start">Yes</div>
                      </div>
                      <div 
                        className={`ls-radio-button ${questions.hasLift === false ? 'selected' : ''}`}
                        onClick={() => handleQuestionChange('hasLift', false)}
                      >
                        <div className="marker"></div>
                        <div className="text text-start">No</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Furniture Question */}
            <div className="open transparent dropdown mb-4">
              <div className="card">
                <div className="card-header dropdown-header">
                  <div className="header-title">
                    Do you need us to move any furniture or large items which require 2 people to carry them?
                  </div>
                </div>
                <div className="dropdown-body">
                  <div className="removal-info-callout">
                    <svg className="removal-info-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p>This includes items such as tables, beds, and sofas.</p>
                  </div>
                  <div className="dropdown-content">
                    <div
                      className={`ls-radio-button ${questions.hasFurniture === true ? 'selected' : ''}`}
                      onClick={() => handleQuestionChange('hasFurniture', true)}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">Yes</div>
                    </div>
                    <div
                      className={`ls-radio-button ${questions.hasFurniture === false ? 'selected' : ''}`}
                      onClick={() => handleQuestionChange('hasFurniture', false)}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">No</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Furniture Disassembly Question - Only show if hasFurniture is true */}
            {questions.hasFurniture === true && (
              <div className="open transparent dropdown mb-4">
                <div className="card">
                  <div className="card-header dropdown-header">
                    <div className="header-title">Would you like us to disassemble any furniture?</div>
                  </div>
                  <div className="dropdown-body">
                    <div className="removal-info-callout removal-info-callout--recommendation">
                      <svg className="removal-info-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <div>
                        <p className="removal-info-callout__title">We recommend our disassembly service for larger furniture items</p>
                        <p>Our professional furniture disassembly service costs <strong>£79</strong> and ensures your items are safely prepared for transport.</p>
                      </div>
                    </div>
                    {questions.hasDisassembly === false && (
                      <div className="removal-info-callout removal-info-callout--warning">
                        <svg className="removal-info-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0377 2.66667 10.2679 4L3.33975 16C2.56995 17.3333 3.53223 19 5.07183 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <p><strong>Important:</strong> If you choose not to use our disassembly service, please ensure all furniture is already disassembled and ready for collection on your move date.</p>
                      </div>
                    )}
                    <div className="dropdown-content">
                      <div
                        className={`ls-radio-button ${questions.hasDisassembly === true ? 'selected' : ''}`}
                        onClick={() => handleQuestionChange('hasDisassembly', true)}
                      >
                        <div className="marker"></div>
                        <div className="text text-start">Yes, please disassemble my furniture</div>
                      </div>
                      <div
                        className={`ls-radio-button ${questions.hasDisassembly === false ? 'selected' : ''}`}
                        onClick={() => handleQuestionChange('hasDisassembly', false)}
                      >
                        <div className="marker"></div>
                        <div className="text text-start">No, I'll handle disassembly myself</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Parking Question */}
            <div className="open transparent dropdown mb-4">
              <div className="card">
                <div className="card-header dropdown-header">
                  <div className="header-title">
                    Does your property have parking?
                  </div>
                </div>
                <div className="dropdown-body">
                  <div className="removal-info-callout removal-info-callout--critical">
                    <svg className="removal-info-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0377 2.66667 10.2679 4L3.33975 16C2.56995 17.3333 3.53223 19 5.07183 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <div>
                      <p className="removal-info-callout__title">Parking is required for our moving team</p>
                      <p>If appropriate parking is not available, we may not be able to collect your items. We recommend obtaining any needed parking permits in advance. Any parking costs incurred during your move will be passed on to you.</p>
                    </div>
                  </div>
                  <div className="dropdown-content">
                    <div
                      className={`ls-radio-button ${questions.hasParking === true ? 'selected' : ''}`}
                      onClick={() => handleQuestionChange('hasParking', true)}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">Yes</div>
                    </div>
                    <div
                      className={`ls-radio-button ${questions.hasParking === false ? 'selected' : ''}`}
                      onClick={() => handleQuestionChange('hasParking', false)}
                    >
                      <div className="marker"></div>
                      <div className="text text-start">No</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="funnelFooter" className="mt-3">
            <div className="sticky-bar">
              <div className="sticky-bar-limit"></div>
              <div className="text-center sticky-bar-body">
                <SubmitButton 
                  disabled={!isFormValid()}
                  onClick={handleContinue}
                >
                  Continue
                </SubmitButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default RemovalsQuestionsComponent;
